import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * Tooltip identifiers for tracking across the application
 */
export enum TooltipId {
  // Modal Generation Tooltips
  MODAL_GENERATION_TOPIC = 'modal_generation_topic',
  MODAL_GENERATION_KEYWORD = 'modal_generation_keyword',

  // Add more tooltip IDs here as needed
  // Example: PRODUCT_NAME = 'product_name',
  // Example: BLOG_EDITOR_TITLE = 'blog_editor_title',
}

/**
 * Tooltip state for individual tooltips
 */
type TooltipState = {
  /**
   * Number of times the tooltip has been dismissed by the user
   */
  dismissCount: number;

  /**
   * Timestamp of the last dismissal
   */
  lastDismissedAt: number | null;

  /**
   * Whether the tooltip should be permanently hidden (after 3 dismissals)
   */
  isPermanentlyHidden: boolean;
};

/**
 * Store state structure
 */
type TooltipStoreState = {
  /**
   * Map of tooltip ID to its state
   */
  tooltips: Record<string, TooltipState>;
};

/**
 * Store actions
 */
type TooltipStoreActions = {
  /**
   * Check if a tooltip should be shown automatically
   * Returns false if the tooltip has been dismissed 3+ times
   */
  shouldShowTooltip: (tooltipId: TooltipId) => boolean;

  /**
   * Record a tooltip dismissal
   * After 3 dismissals, the tooltip will be permanently hidden
   */
  dismissTooltip: (tooltipId: TooltipId) => void;

  /**
   * Get the current state of a tooltip
   */
  getTooltipState: (tooltipId: TooltipId) => TooltipState;

  /**
   * Reset a specific tooltip's state (useful for testing or admin purposes)
   */
  resetTooltip: (tooltipId: TooltipId) => void;

  /**
   * Reset all tooltips (useful for testing or admin purposes)
   */
  resetAllTooltips: () => void;

  /**
   * Manually set a tooltip as permanently hidden
   */
  hideTooltipPermanently: (tooltipId: TooltipId) => void;
};

type FullTooltipStore = TooltipStoreState & TooltipStoreActions;

/**
 * Maximum number of dismissals before a tooltip is permanently hidden
 */
const MAX_DISMISSALS = 3;

/**
 * Default state for a new tooltip
 */
const DEFAULT_TOOLTIP_STATE: TooltipState = {
  dismissCount: 0,
  lastDismissedAt: null,
  isPermanentlyHidden: false,
};

/**
 * Zustand store for managing tooltip visibility across sessions
 *
 * Features:
 * - Persists tooltip state to localStorage
 * - Tracks dismiss count per tooltip
 * - Automatically hides tooltips after 3 dismissals
 * - Supports wide range of tooltip entries via TooltipId enum
 */
export const useTooltipStore = create<FullTooltipStore>()(
  devtools(
    persist(
      function (set, get) {
        return {
          // Initial state
          tooltips: {},

          // Actions
          shouldShowTooltip: function (tooltipId: TooltipId): boolean {
            const state = get().getTooltipState(tooltipId);
            return !state.isPermanentlyHidden && state.dismissCount < MAX_DISMISSALS;
          },

          dismissTooltip: function (tooltipId: TooltipId): void {
            const currentState = get().getTooltipState(tooltipId);
            const newDismissCount = currentState.dismissCount + 1;
            const isPermanentlyHidden = newDismissCount >= MAX_DISMISSALS;

            set(function (state) {
              return {
                tooltips: {
                  ...state.tooltips,
                  [tooltipId]: {
                    dismissCount: newDismissCount,
                    lastDismissedAt: Date.now(),
                    isPermanentlyHidden,
                  },
                },
              };
            });
          },

          getTooltipState: function (tooltipId: TooltipId): TooltipState {
            const state = get().tooltips[tooltipId];
            return state ?? DEFAULT_TOOLTIP_STATE;
          },

          resetTooltip: function (tooltipId: TooltipId): void {
            set(function (state) {
              const newTooltips = { ...state.tooltips };
              delete newTooltips[tooltipId];
              return {
                tooltips: newTooltips,
              };
            });
          },

          resetAllTooltips: function (): void {
            set({ tooltips: {} });
          },

          hideTooltipPermanently: function (tooltipId: TooltipId): void {
            set(function (state) {
              return {
                tooltips: {
                  ...state.tooltips,
                  [tooltipId]: {
                    ...get().getTooltipState(tooltipId),
                    isPermanentlyHidden: true,
                  },
                },
              };
            });
          },
        };
      },
      {
        name: 'blym-tooltips-storage',
        version: 1,
      }
    ),
    { name: 'blym', store: 'tooltips' }
  )
);
